import { useState } from "react";
import { Link } from "react-router-dom";
import { QrCode, ArrowLeft, Loader2, MailCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [devLink, setDevLink] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErr("");
    try {
      const { data } = await api.post("/auth/forgot-password", { email: email.trim() });
      setSubmitted(true);
      if (data?.dev_link) {
        setDevLink(data.dev_link);
      }
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <div className="max-w-md mx-auto w-full px-4 sm:px-6 flex-1 flex flex-col justify-center py-12">
        <Link to="/" className="flex items-center gap-2.5 justify-center mb-8">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-600 to-cyan-500 flex items-center justify-center shadow">
            <QrCode className="w-5 h-5 text-white" strokeWidth={2.5} />
          </div>
          <span className="font-black text-2xl tracking-tight text-slate-900">Qdurator</span>
        </Link>

        <div className="bg-white border border-slate-200 rounded-2xl p-8 shadow-sm">
          {submitted ? (
            <div data-testid="forgot-password-sent">
              <div className="w-12 h-12 rounded-2xl bg-emerald-50 border border-emerald-200 flex items-center justify-center">
                <MailCheck className="w-6 h-6 text-emerald-600" />
              </div>
              <h1 className="mt-4 text-2xl font-bold tracking-tight text-slate-900">
                Check your inbox
              </h1>
              <p className="mt-2 text-sm text-slate-500">
                If an account exists for <span className="font-semibold">{email}</span>, we've sent a link to reset the password. It expires in one hour.
              </p>

              {devLink && (
                <div className="mt-6 p-4 rounded-xl bg-amber-50 border border-amber-200" data-testid="dev-reset-link-box">
                  <div className="text-xs font-bold uppercase tracking-wider text-amber-700">
                    Dev mode — no email configured
                  </div>
                  <p className="text-xs text-amber-800 mt-1">
                    Add <code className="font-mono">RESEND_API_KEY</code> to the backend .env to send real emails. For now, use this link:
                  </p>
                  <a
                    href={devLink}
                    className="mt-2 block break-all text-xs font-mono text-blue-700 hover:underline"
                    data-testid="dev-reset-link"
                  >
                    {devLink}
                  </a>
                </div>
              )}

              <Link
                to="/login"
                className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-blue-600 hover:text-blue-700"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to log in
              </Link>
            </div>
          ) : (
            <>
              <h1 className="text-2xl font-bold tracking-tight text-slate-900" data-testid="forgot-password-heading">
                Reset your password
              </h1>
              <p className="text-sm text-slate-500 mt-1">
                Enter your email and we'll send you a reset link.
              </p>
              <form onSubmit={onSubmit} className="mt-6 space-y-5">
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1.5 block">
                    Email
                  </Label>
                  <Input
                    data-testid="forgot-password-email-input"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    className="h-11 rounded-xl"
                  />
                </div>
                {err && (
                  <div className="text-sm bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg">
                    {err}
                  </div>
                )}
                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="forgot-password-submit-btn"
                  className="w-full h-11 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send reset link"}
                </Button>
              </form>

              <div className="mt-6 text-center">
                <Link to="/login" className="text-sm text-slate-500 hover:text-slate-700">
                  ← Back to log in
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
